/**
 * F044 Verdict, Voice & Score Architecture — the generated-by-hand TS mirror of the C# vocabulary
 * (`LeftShield.Domain.Model.Presentation.EnforcementVerdict` / `FeatureLifecycleState`), and the
 * source-of-truth `tools/glossary.json`.
 *
 * DELTA FROM SPEC: the spec asks for a build-time code-generation step (`verdict-kit/vocabulary/
 * generated from glossary.json`) with a contract test proving the generated TS enum equals the C#
 * enum on every build. No codegen pipeline exists in this repo for a cross-language enum mirror
 * (there is no shared IDL/OpenAPI-enum step wired into `npm run build`), so this file is a
 * HAND-MAINTAINED mirror instead — the parity is enforced by
 * `src/components/verdict/vocabulary.test.ts`, which asserts this list against the same
 * `tools/glossary.json` the CI vocabulary lint reads, catching JSON/TS drift. C#-vs-TS parity is a
 * flagged owner decision (see F044-PROGRESS.md) until a real codegen step exists.
 */
import { verdictTokens, type VerdictTokens } from '../../theme/solution-theme';
import type { ColorIntent, EnforcementVerdictWord, TenantStateWord, TrendDirection } from '../../types';

/** SOURCED-FROM F034 REGE (`RegeVerdict`, collapsed by `EnforcementVerdictMap`). Ordered exactly as
 *  the C# enum so ordinal-sensitive code (none currently) stays aligned. */
export const ENFORCEMENT_VERDICTS: readonly EnforcementVerdictWord[] = [
  'Allow',
  'Allow with Constraints',
  'Approval Required',
  'Sandbox',
  'Quarantine',
  'Block',
];

/** F043 `FeatureLifecycleState` display words — UI aliases, never a third synonym set. */
export const TENANT_STATE_WORDS: readonly TenantStateWord[] = [
  'Not Connected',
  'Connected',
  'Collecting',
  'Active',
  'Degraded',
  'Unavailable',
];

/** Colour discipline: only Block/Quarantine are red; only Allow is green; the middle is amber. Mirrors
 *  `EnforcementVerdictExtensions.ToColorIntent` in C# — the ONE place this mapping is allowed to exist. */
export function enforcementColorIntent(word: EnforcementVerdictWord): ColorIntent {
  switch (word) {
    case 'Allow':
      return 'Positive';
    case 'Block':
    case 'Quarantine':
      return 'Failure';
    case 'Allow with Constraints':
    case 'Approval Required':
    case 'Sandbox':
      return 'Caution';
  }
}

/** A state is ALWAYS neutral grey — never a false all-clear (green), never a false crisis (red). */
export function stateColorIntent(_state: TenantStateWord): ColorIntent {
  return 'Neutral';
}

/** The ONLY function permitted to map `ColorIntent` to a Fluent semantic token. No component may
 *  reach past this into `verdictTokens` directly for a foreground colour — keeps the mapping in one place. */
export function colorIntentToken(intent: ColorIntent): { foreground: string; background: string } {
  const map: Record<ColorIntent, { foreground: keyof VerdictTokens; background: keyof VerdictTokens }> = {
    Neutral: { foreground: 'verdictNeutral', background: 'verdictNeutralBackground' },
    Positive: { foreground: 'verdictPositive', background: 'verdictPositiveBackground' },
    Caution: { foreground: 'verdictCaution', background: 'verdictCautionBackground' },
    Failure: { foreground: 'verdictFailure', background: 'verdictFailureBackground' },
  };
  const { foreground, background } = map[intent];
  return { foreground: verdictTokens[foreground], background: verdictTokens[background] };
}

/** Trend rule: delta === 0 -> `TrendDirection.None` -> `ColorIntent.Neutral` (grey), ALWAYS —
 *  closes the "+0 green vs +0 red" defect (F044 findings A-02 / I-04). */
export function resolveTrend(delta: number, higherIsBetter: boolean): TrendDirection {
  if (delta === 0) return 'None';
  const better = higherIsBetter ? delta > 0 : delta < 0;
  return better ? 'Improving' : 'Worsening';
}

export function trendColorIntent(direction: TrendDirection): ColorIntent {
  switch (direction) {
    case 'None':
      return 'Neutral';
    case 'Improving':
      return 'Positive';
    case 'Worsening':
      return 'Failure';
  }
}
